import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { LoadingProvider } from './context/LoadingContext';

// Theme
import theme from './app/util/theme';

// Layout Components

// Auth Components
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Pages
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import Dashboard from './pages/dashboard/Dashboard';
import DevicesPage from './pages/devices/DevicesPage';
import DeviceGroupsPage from './pages/devices/DeviceGroupsPage';
import ContentPage from './pages/content/ContentPage';
import LayoutsPage from './pages/layouts/LayoutsPage';
import SchedulingPage from './pages/scheduling/SchedulingPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import UserManagementPage from './pages/users/UserManagementPage';
import SettingsPage from './pages/settings/SettingsPage';
import PreviewPage from './pages/preview/PreviewPage';

// Notification Component
const NotificationContainer = () => {
  // This could be enhanced with actual notification display logic
  return null;
};

function App() {
  return (
    <CssVarsProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <LoadingProvider>
            <Router>
              <div className="App">
                <NotificationContainer />
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                  {/* Protected Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/devices"
                    element={
                      <ProtectedRoute requiredPermission="manage_devices">
                        <DevicesPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/device-groups"
                    element={
                      <ProtectedRoute requiredPermission="manage_devices">
                        <DeviceGroupsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/content"
                    element={
                      <ProtectedRoute requiredPermission="manage_content">
                        <ContentPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/layouts"
                    element={
                      <ProtectedRoute requiredPermission="manage_layouts">
                        <LayoutsPage />
                      </ProtectedRoute>
                    }
                  />



                  <Route
                    path="/scheduling"
                    element={
                      <ProtectedRoute requiredPermission="manage_schedules">
                        <SchedulingPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute adminOnly>
                        <UserManagementPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute adminOnly>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/change-password"
                    element={
                      <ProtectedRoute>
                        <ChangePasswordPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/preview"
                    element={
                      <ProtectedRoute>
                        <PreviewPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirect to login */}
                  <Route path="/" element={<Navigate to="/login" replace />} />

                  {/* Catch all route - redirect to login */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </div>
            </Router>
          </LoadingProvider>
        </NotificationProvider>
      </AuthProvider>
    </CssVarsProvider>
  );
}

export default App;